import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance, NumberInput, Checkbox} from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import PageContent from '@/components/PageContent';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import {  SubscriptionCode, UpsertSubscriptionCode } from '@/models/subscription-code.model';
import { upsertSubscriptionCode } from '@/services/subscription-code.service';
import FetchingSelect from '@/components/FetchingSelect';
import { getSubscriptionPlansLookup } from '@/services/subscription-plan.service';
import ZonedDatePicker from '@/components/ZonedDatePicker';
import { utc_iana_time_zone_id } from '@/constants/time';

const subscriptionCodeSchema = Schema.Model<UpsertSubscriptionCode>({
  id: Schema.Types.NumberType(),
  subscriptionPlanId: Schema.Types.NumberType().isRequired('This field is required.'),
  code: Schema.Types.StringType().isRequired('This field is required.'),
  maxUses: Schema.Types.NumberType().isRequired('This field is required.'),
  startDate: Schema.Types.DateType().isRequired('This field is required.'),
  endDate: Schema.Types.DateType(),
  isActive: Schema.Types.BooleanType().isRequired('This field is required.'),
});


const emptyModel: UpsertSubscriptionCode = {
  id: undefined,
  subscriptionPlanId: undefined,
  code: '',
  maxUses: 1,
  startDate: undefined,
  endDate: undefined,
  isActive: true
};

interface SubscriptionCodeFormProps{
  formObject?: SubscriptionCode;
  pageState: PageState;
  subscriptionPlanId?: number;
};

const SubscriptionCodeForm: React.FC<SubscriptionCodeFormProps> = ({formObject, pageState, subscriptionPlanId }) => {
  const [formValue, setFormValue] = useState<UpsertSubscriptionCode>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if(pageState == PageState.New){
      setFormValue({...emptyModel, subscriptionPlanId});
      return;
    }

    if (formObject) {
      setFormValue(formObject);
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject]);

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<SubscriptionCode> = await upsertSubscriptionCode(formValue);
      if (result.success) {
        handleBackPage();
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  const handleBackPage = () => {
    subscriptionPlanId ? 
      nav(`/subscription/plans/${subscriptionPlanId}`, {state: {tab: 'subscription-redeem-codes'}}) : 
      nav(-1);
  };

  return (
    <PageContent>
      <Form name='subscription-code-form' fluid
        ref={formRef}
        onChange={handleFormChange}
        onCheck={setFormError}
        formValue={formValue}
        model={subscriptionCodeSchema}
        disabled={loading}
      >
        <Grid w='100%' fluid >
          <Row justifyContent="center">
            <Col span={{xs:24, xl:12}}>
              <Form.Group>
                <Form.Label>Subscription Plan</Form.Label>
                <Form.Control name="subscriptionPlanId" 
                  accepter={FetchingSelect} block
                  fetchData={(text, page) => getSubscriptionPlansLookup(text, page)}
                  placeholder="Select a plan"
                  disabled={!!subscriptionPlanId}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row justifyContent="center">
            <Col span={{xs:24, xl:6}}>
              <Form.Group>
                <Form.Label>Code</Form.Label>
                <Form.Control name="code"/>
              </Form.Group>
            </Col>
            <Col span={{xs:24, xl:6}}>
              <Form.Group>
                <Form.Label>Max Uses</Form.Label>
                <Form.Control name="maxUses" accepter={NumberInput} min={1}/>
              </Form.Group>
            </Col>
          </Row>
          <Row justifyContent="center">
            <Col span={{xs:24, xl:6}}>
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                  <Form.Control accepter={ZonedDatePicker} block
                    name="startDate" 
                    value={formValue.startDate ?? null}
                    onChange={dt => setFormValue(pre => ({...pre, startDate: dt ?? undefined}))}
                    zoneIanaId={utc_iana_time_zone_id}
                  />                  
              </Form.Group>
            </Col>
            <Col span={{xs:24, xl:6}}>
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                  <Form.Control accepter={ZonedDatePicker} block
                    name="endDate" 
                    value={formValue.endDate ?? null}
                    onChange={dt => setFormValue(pre => ({...pre, endDate: dt ?? undefined}))}
                    zoneIanaId={utc_iana_time_zone_id}
                  />                  
              </Form.Group>
            </Col>
          </Row>
          <Row justifyContent="center">
            <Col span={{xs:24, xl:12}} >
              <Form.Group>
                <Form.Label>Is Active</Form.Label>
                <Form.Control name="isActive" accepter={Checkbox} 
                  checked={!!formValue.isActive} 
                  onChange={(val, chk) => setFormValue(pre => ({ ...pre, isActive: chk }))}
                />                  
              </Form.Group>                   
            </Col>
          </Row> 
        </Grid>
      </Form>
      <Divider />
      {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}

      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={handleBackPage}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default SubscriptionCodeForm;

